/**
 * AppICEMAP — ProfileScreen
 * Custom element: `<profile-screen>`
 *
 * Full-screen profile/settings panel showing:
 *   - Display name, avatar fallback (initials)
 *   - Karma score with progress bar to next milestone
 *   - Strikes / limited visibility warning
 *   - Settings links (stats, notifications, theme toggle)
 *   - Logout button
 *
 * Events:
 *   profile:close   → { }
 *   profile:logout  → { }
 */

import { state, dispatch, saveRadius } from "../../store/index.js";

import {
	getUnit,
	setUnit,
	radiusOptions,
	formatRadius,
} from "../../utils/units.js";
import { logout } from "../../api/auth.js";
import { setTheme } from "../../utils/theme.js";
import router from "../../router.js";
// Shadow DOM encapsulates styles — inject the global profile.css inside.
import profileCss from "../../../css/components/profile.css?inline";
// Phosphor's `.ph` rules live at document scope and don't cross the shadow
// boundary, so the glyphs need re-declaring inside the shadow root.
import iconCss from "../../../css/utilities/phosphor-shadow.css?inline";

const template = document.createElement("template");
template.innerHTML = `
  <div class="profile-screen">
    <header class="profile-screen__header">
      <button class="btn btn--icon" aria-label="Volver" data-action="back">
        <i class="ph ph-caret-left" aria-hidden="true"></i>
      </button>
      <h2 id="profile-title">Mi perfil</h2>
    </header>

    <div class="profile-screen__content">
    <div class="profile-view" data-view="main">
      <!-- Identity -->
      <section class="profile-hero">
        <div class="profile-hero__avatar" id="profile-avatar">
          <span class="profile-hero__avatar-text" id="profile-avatar-text"></span>
        </div>
        <h3 class="profile-hero__name" id="profile-name"></h3>
        <p class="profile-hero__email" id="profile-email" hidden>
          <i class="ph ph-envelope-simple" aria-hidden="true"></i>
          <span id="profile-email-text"></span>
        </p>
      </section>

      <!-- Karma -->
      <section class="karma-card" id="profile-karma-card">
        <div class="karma-card__top">
          <div class="karma-card__score">
            <span class="karma-card__number" id="profile-karma-num">0</span>
            <span class="karma-card__label">karma</span>
          </div>
          <span class="karma-card__level" id="profile-karma-level">
            <i class="ph ph-medal" aria-hidden="true"></i>
            <span id="profile-karma-level-text">Novato</span>
          </span>
        </div>

        <div class="karma-card__progress">
          <div class="karma-bar" role="progressbar" id="profile-karma-bar"
               aria-valuemin="0" aria-valuemax="100" aria-valuenow="0">
            <div class="karma-bar__fill" id="profile-karma-bar-fill"></div>
          </div>
          <span class="karma-card__milestone-label" id="profile-karma-next"></span>
        </div>

        <div class="karma-card__limited" role="alert" id="profile-limited" hidden>
          <i class="ph ph-warning" aria-hidden="true"></i>
          <span id="profile-limited-text"></span>
        </div>
      </section>

      <!-- Account stats -->
      <section class="profile-stats" id="profile-stats" aria-label="Resumen de la cuenta">
        <div class="profile-stat">
          <i class="ph ph-trophy" aria-hidden="true"></i>
          <span class="profile-stat__value" id="stat-karma">0</span>
          <span class="profile-stat__label">Karma</span>
        </div>
        <div class="profile-stat">
          <i class="ph ph-warning" aria-hidden="true"></i>
          <span class="profile-stat__value" id="stat-strikes">0</span>
          <span class="profile-stat__label">Avisos</span>
        </div>
        <div class="profile-stat">
          <i class="ph ph-shield-check" aria-hidden="true"></i>
          <span class="profile-stat__value" id="stat-status">Activo</span>
          <span class="profile-stat__label">Estado</span>
        </div>
      </section>

      <!-- Menu items -->
      <nav class="profile-menu" role="navigation" aria-label="Configuración">
        <a class="profile-menu__item" href="#stats" data-action="go-stats">
          <i class="ph ph-chart-bar" aria-hidden="true"></i>
          <span>Mis estadísticas</span>
          <i class="ph ph-caret-right profile-menu__chevron" aria-hidden="true"></i>
        </a>

        <button class="profile-menu__item" data-action="toggle-theme">
          <i class="ph ph-moon-stars" aria-hidden="true" id="theme-icon"></i>
          <span id="theme-label">Tema</span>
          <span class="profile-menu__hint" id="theme-hint">Noche</span>
        </button>

        <button class="profile-menu__item" data-action="toggle-unit">
          <i class="ph ph-ruler" aria-hidden="true"></i>
          <span>Unidades de distancia</span>
          <span class="profile-menu__hint" id="unit-value">Millas</span>
        </button>

        <div class="profile-menu__item profile-menu__item--static">
          <i class="ph ph-crosshair" aria-hidden="true"></i>
          <span>Reportes cercanos</span>
          <span class="profile-menu__hint" id="radius-value">Sin límite</span>
        </div>
        <div class="radius-control">
          <input type="range" class="radius-control__slider" id="radius-slider"
                 min="0" max="5" step="1" value="0"
                 aria-label="Radio de reportes cercanos">
          <div class="radius-control__scale" id="radius-scale" aria-hidden="true"></div>
          <p class="radius-control__help" id="radius-help"></p>
        </div>

        <button class="profile-menu__item" data-action="open-help">
          <i class="ph ph-question" aria-hidden="true"></i>
          <span>Ayuda</span>
          <i class="ph ph-caret-right profile-menu__chevron" aria-hidden="true"></i>
        </button>

        <button class="profile-menu__item" data-action="open-about">
          <i class="ph ph-info" aria-hidden="true"></i>
          <span>Acerca de</span>
          <i class="ph ph-caret-right profile-menu__chevron" aria-hidden="true"></i>
        </button>
      </nav>

      <!-- Logout -->
      <button class="profile-logout" data-action="logout">
        <i class="ph ph-sign-out" aria-hidden="true"></i>
        Cerrar sesión
      </button>
      </div><!-- /main view -->

      <!-- ══ Ayuda ═══════════════════════════════════════════════════════ -->
      <div class="profile-view" data-view="help" hidden>
        <p class="info-lead">
          ICEMAP es un mapa comunitario de alertas en tiempo real. Cualquiera
          puede ver el mapa; para publicar, confirmar o comentar necesitas una
          cuenta.
        </p>

        <h3 class="info-heading">Los 7 tipos de reporte</h3>
        <ul class="report-types">
          <li class="report-type">
            <span class="report-type__icon">🧊</span>
            <div class="report-type__body">
              <span class="report-type__name">ICE <span class="report-type__ttl">30 min</span></span>
              <p class="report-type__desc">Presencia u operativos de control migratorio (ICE): redadas, retenes o vehículos. Es el aviso más urgente y dura poco a propósito, para que refleje algo que está pasando ahora y la comunidad pueda evitar la zona.</p>
            </div>
          </li>
          <li class="report-type">
            <span class="report-type__icon">👮‍♀️</span>
            <div class="report-type__body">
              <span class="report-type__name">Policía <span class="report-type__ttl">1 h</span></span>
              <p class="report-type__desc">Presencia policial, controles o patrullas en la zona.</p>
            </div>
          </li>
          <li class="report-type">
            <span class="report-type__icon">⚠️</span>
            <div class="report-type__body">
              <span class="report-type__name">Alerta de seguridad <span class="report-type__ttl">2 h</span></span>
              <p class="report-type__desc">Una situación peligrosa cerca: robos, violencia o cualquier riesgo del que convenga avisar a los vecinos.</p>
            </div>
          </li>
          <li class="report-type">
            <span class="report-type__icon">🚨</span>
            <div class="report-type__body">
              <span class="report-type__name">Accidente <span class="report-type__ttl">2 h</span></span>
              <p class="report-type__desc">Un accidente de tráfico que afecta la circulación o requiere precaución al pasar.</p>
            </div>
          </li>
          <li class="report-type">
            <span class="report-type__icon">🕳️</span>
            <div class="report-type__body">
              <span class="report-type__name">Bache <span class="report-type__ttl">8 h</span></span>
              <p class="report-type__desc">Un bache o daño en el asfalto que conviene esquivar.</p>
            </div>
          </li>
          <li class="report-type">
            <span class="report-type__icon">🚦</span>
            <div class="report-type__body">
              <span class="report-type__name">Semáforo dañado <span class="report-type__ttl">4 h</span></span>
              <p class="report-type__desc">Un semáforo averiado o un cruce sin señalización funcionando.</p>
            </div>
          </li>
          <li class="report-type">
            <span class="report-type__icon">🚧</span>
            <div class="report-type__body">
              <span class="report-type__name">Obras <span class="report-type__ttl">24 h</span></span>
              <p class="report-type__desc">Obras o construcción que cortan o dificultan el paso.</p>
            </div>
          </li>
        </ul>

        <h3 class="info-heading">Cómo funciona un reporte</h3>
        <ul class="info-list">
          <li><strong>Caduca solo.</strong> Cada tipo dura un tiempo (el que ves arriba). Al vencer, primero pasa a “expirando” y luego desaparece del mapa, para que la información sea siempre reciente.</li>
          <li><strong>La comunidad lo valida.</strong> Otros usuarios pueden <em>confirmar</em> 👍 o <em>descartar</em> 👎 cada reporte. Eso ajusta su credibilidad y el karma de quien lo publicó.</li>
          <li><strong>Anónimo o con tu nombre.</strong> Al publicar eliges si aparece tu nombre o “Anónimo”. En modo anónimo <em>nadie</em> —ni siquiera otros usuarios— puede ver quién lo creó.</li>
          <li><strong>Ubicación exacta o aproximada.</strong> Puedes marcar la posición exacta o difuminarla (~200 m) para cuidar tu privacidad.</li>
        </ul>

        <h3 class="info-heading">Registro y limitaciones</h3>
        <ul class="info-list">
          <li><strong>Registro inmediato.</strong> No pedimos verificación por correo: tu cuenta queda activa al instante. Como no hay verificación, tampoco hay recuperación de contraseña — guarda bien tu clave.</li>
          <li><strong>Karma.</strong> Cuando la comunidad confirma tus reportes ganas <strong>+1</strong>; si los descartan, <strong>−1</strong>. Vas subiendo de nivel: Novato → Vecino → Colaborador → Confiable → Guardián → Leyenda (10, 50, 100, 250 y 500 puntos).</li>
          <li><strong>Visibilidad limitada.</strong> Si acumulas descartes, tu cuenta puede quedar “limitada” y tus reportes se muestran con menos peso hasta que la comunidad los confirme. Para restaurarla necesitas <strong>3 reportes confirmados</strong>.</li>
          <li><strong>Por qué existe esto.</strong> Mantener la información fiable y proteger a la comunidad de reportes falsos. Tu privacidad como autor está garantizada por el sistema, no solo por la pantalla.</li>
        </ul>
      </div><!-- /help view -->

      <!-- ══ Acerca de ═══════════════════════════════════════════════════ -->
      <div class="profile-view" data-view="about" hidden>
        <section class="about-card">
          <div class="about-logo">🧊🗺️</div>
          <h3 class="about-title">ICEMAP</h3>
          <p class="about-tagline">Mapa comunitario de alertas en tiempo real</p>
          <span class="about-version">Versión 1.2</span>
        </section>

        <p class="about-credit">
          Creado por <strong>FeresDev</strong><br />
          <span class="about-love">con amor para la comunidad migrante ❤️</span>
        </p>

        <p class="about-note">
          Estamos en la tierra que Dios dejó para todos sus hijos, y no deberían
          existir limitaciones en ella.
        </p>

        <blockquote class="about-verse">
          «Y los bendijo Dios, y díjoles: Fructificad y multiplicad, y henchid la
          tierra, y sojuzgadla, y señoread en los peces de la mar, y en las aves
          del cielo, y en todas las bestias que se mueven sobre la tierra».
          <cite>Génesis 1:28 (RVR1960)</cite>
        </blockquote>
      </div><!-- /about view -->

    </div>
  </div>
`;

export class ProfileScreen extends HTMLElement {
	constructor() {
		super();
		/** @type {"main"|"help"|"about"} */
		this._currentView = "main";
		this.attachShadow({ mode: "open" });
		const style = document.createElement("style");
		style.textContent = `${iconCss}\n${profileCss}`;
		this.shadowRoot.appendChild(style);
		this.shadowRoot.appendChild(template.content.cloneNode(true));
	}

	connectedCallback() {
		this._render();
		this._bindEvents();
	}

	/* ── Public API ──────────────────────────────────────────────────────── */

	show() {
		this.style.display = "block";
		// Always reopen on the main menu, never on a leftover sub-view.
		this._showView("main");
		this._render();
	}

	hide() {
		this.style.display = "none";
	}

	/* ── View switching (main / help / about) ────────────────────────────── */

	/** @param {"main"|"help"|"about"} name */
	_showView(name) {
		this._currentView = name;

		const titles = { main: "Mi perfil", help: "Ayuda", about: "Acerca de" };
		const titleEl = this.shadowRoot.querySelector("#profile-title");
		if (titleEl) titleEl.textContent = titles[name] || "Mi perfil";

		this.shadowRoot.querySelectorAll(".profile-view").forEach((el) => {
			el.hidden = el.dataset.view !== name;
		});

		// Jump back to the top when swapping views.
		this.shadowRoot.querySelector(".profile-screen")?.scrollTo?.(0, 0);
	}

	/* ── Private ─────────────────────────────────────────────────────────── */

	_bindEvents() {
		this._bindRadiusControl();

		this.shadowRoot
			.querySelector('[data-action="back"]')
			?.addEventListener("click", () => {
				// Inside Ayuda/Acerca de, "back" returns to the menu; from the
				// menu it closes the whole panel.
				if (this._currentView && this._currentView !== "main") {
					this._showView("main");
				} else {
					this._close();
				}
			});

		this.shadowRoot
			.querySelector('[data-action="open-help"]')
			?.addEventListener("click", () => this._showView("help"));

		this.shadowRoot
			.querySelector('[data-action="open-about"]')
			?.addEventListener("click", () => this._showView("about"));

		this.shadowRoot
			.querySelector('[data-action="logout"]')
			?.addEventListener("click", async () => {
				await this._handleLogout();
			});

		this.shadowRoot
			.querySelector('[data-action="toggle-theme"]')
			?.addEventListener("click", () => {
				this._toggleTheme();
			});

		this.shadowRoot
			.querySelector('[data-action="go-stats"]')
			?.addEventListener("click", (e) => {
				e.preventDefault();
				this.hide();
				router.navigate("/stats");
			});
	}

	/* ── Radio de cercanía ─────────────────────────────────────────────── */

	_bindRadiusControl() {
		const slider = this.shadowRoot.querySelector("#radius-slider");
		if (!slider) return;

		this._syncRadiusControl();

		// Etiqueta en vivo mientras se arrastra; el estado solo se toca al soltar
		slider.addEventListener("input", () => this._renderRadiusLabel());

		slider.addEventListener("change", () => {
			const km = this._radiusOptions()[Number(slider.value)]?.km ?? null;
			saveRadius(km);
			dispatch("SET_FILTER", { radiusKm: km });
			this._renderRadiusLabel();
		});

		this.shadowRoot
			.querySelector('[data-action="toggle-unit"]')
			?.addEventListener("click", () => this._toggleUnit());
	}

	/** Opciones del deslizador en la unidad activa */
	_radiusOptions() {
		return radiusOptions(getUnit());
	}

	/**
	 * Coloca el deslizador en la preferencia guardada y pinta la escala.
	 * El radio se guarda SIEMPRE en km, así que al cambiar de unidad el punto
	 * elegido no se mueve: solo cambia cómo se escribe.
	 */
	_syncRadiusControl() {
		const slider = this.shadowRoot.querySelector("#radius-slider");
		const scaleEl = this.shadowRoot.querySelector("#radius-scale");
		const unitEl = this.shadowRoot.querySelector("#unit-value");
		if (!slider) return;

		const options = this._radiusOptions();
		slider.max = String(options.length - 1);

		if (scaleEl) {
			scaleEl.innerHTML = options.map((o) => `<span>${o.label}</span>`).join("");
		}
		if (unitEl) {
			unitEl.textContent = getUnit() === "mi" ? "Millas" : "Kilómetros";
		}

		// El índice más cercano al radio guardado
		const current = state.filters?.radiusKm ?? null;
		let idx = options.length - 1;
		if (current !== null) {
			let best = Infinity;
			options.forEach((o, k) => {
				if (o.km === null) return;
				const diff = Math.abs(o.km - current);
				if (diff < best) {
					best = diff;
					idx = k;
				}
			});
		}
		slider.value = String(idx);

		this._renderRadiusLabel();
	}

	_toggleUnit() {
		setUnit(getUnit() === "mi" ? "km" : "mi");
		this._syncRadiusControl();

		// El deslizador se ajusta al paso más cercano de la nueva unidad (5 mi
		// cae en 10 km), así que hay que GUARDAR ese valor: si no, la etiqueta
		// diría "10 km" mientras el filtro seguiría a 8,05 km.
		const slider = this.shadowRoot.querySelector("#radius-slider");
		const km = this._radiusOptions()[Number(slider?.value)]?.km ?? null;
		saveRadius(km);

		// Además, las tarjetas ya pintadas muestran la distancia en la unidad
		// anterior: al despachar se repinta la lista entera.
		dispatch("SET_FILTER", { ...state.filters, radiusKm: km });
	}

	_renderRadiusLabel() {
		const slider = this.shadowRoot.querySelector("#radius-slider");
		const valueEl = this.shadowRoot.querySelector("#radius-value");
		const helpEl = this.shadowRoot.querySelector("#radius-help");
		if (!slider) return;

		const option = this._radiusOptions()[Number(slider.value)];
		const km = option?.km ?? null;
		const texto = formatRadius(km);

		if (valueEl) valueEl.textContent = texto;
		if (helpEl) {
			helpEl.textContent =
				km === null
					? "Sin límite: la lista incluye también lo que estés viendo en el mapa."
					: `La lista solo mostrará reportes a menos de ${texto} de ti, mires donde mires el mapa.`;
		}
	}

	_render() {
		const user = state.user;

		if (!user) {
			this._renderGuest();
			return;
		}

		// Avatar initials
		const initials = this._getInitials(user);
		const avatarEl = this.shadowRoot.querySelector("#profile-avatar-text");
		if (avatarEl) avatarEl.textContent = initials;

		// Name
		const nameEl = this.shadowRoot.querySelector("#profile-name");
		if (nameEl) {
			const displayName =
				[user.first_name, user.last_name].filter(Boolean).join(" ") ||
				"Usuario";
			nameEl.textContent = displayName;
		}

		// Email
		const emailEl = this.shadowRoot.querySelector("#profile-email");
		const emailTextEl = this.shadowRoot.querySelector("#profile-email-text");
		if (emailEl && emailTextEl) {
			emailEl.hidden = !user.email;
			emailTextEl.textContent = user.email || "";
		}

		// Karma
		const karma = user.karma ?? 0;
		const strikes = user.strikes ?? 0;
		const isLimited = user.is_limited ?? false;
		const rehab = user.rehabilitations ?? 0;

		const karmaNumEl = this.shadowRoot.querySelector("#profile-karma-num");
		if (karmaNumEl) {
			karmaNumEl.textContent = String(karma);
			karmaNumEl.classList.remove("karma-card__number--empty");
		}

		// Next milestone
		const { nextMilestone, progress, level } = this._calcKarma(karma);

		const fillEl = this.shadowRoot.querySelector("#profile-karma-bar-fill");
		if (fillEl) fillEl.style.width = `${progress}%`;

		const barEl = this.shadowRoot.querySelector("#profile-karma-bar");
		if (barEl) {
			barEl.setAttribute("aria-valuenow", String(Math.round(progress)));
			barEl.setAttribute(
				"aria-label",
				`Progreso hacia ${nextMilestone} puntos de karma`,
			);
		}

		const levelEl = this.shadowRoot.querySelector("#profile-karma-level-text");
		if (levelEl) levelEl.textContent = level;

		const nextEl = this.shadowRoot.querySelector("#profile-karma-next");
		if (nextEl) {
			const missing = Math.max(0, nextMilestone - karma);
			nextEl.textContent = `Te faltan ${missing} pts para llegar a ${nextMilestone}`;
		}

		// Limited warning
		const limitedEl = this.shadowRoot.querySelector("#profile-limited");
		const limitedTextEl = this.shadowRoot.querySelector(
			"#profile-limited-text",
		);
		if (limitedEl && limitedTextEl) {
			if (isLimited) {
				limitedEl.hidden = false;
				const remaining = Math.max(0, 3 - rehab);
				limitedTextEl.textContent = `Visibilidad limitada — ${remaining} reportes para restaurar`;
			} else {
				limitedEl.hidden = true;
			}
		}

		// Stat tiles
		this._setStat("#stat-karma", String(karma));
		this._setStat("#stat-strikes", String(strikes));
		this._setStat("#stat-status", isLimited ? "Limitado" : "Activo");

		const statusTile = this.shadowRoot.querySelector("#stat-status");
		statusTile
			?.closest(".profile-stat")
			?.classList.toggle("profile-stat--warn", isLimited);

		const strikesTile = this.shadowRoot.querySelector("#stat-strikes");
		strikesTile
			?.closest(".profile-stat")
			?.classList.toggle("profile-stat--warn", strikes > 0);

		// Theme toggle
		this._renderThemeToggle();
	}

	/**
	 * @param {string} selector
	 * @param {string} value
	 */
	_setStat(selector, value) {
		const el = this.shadowRoot.querySelector(selector);
		if (el) el.textContent = value;
	}

	_renderGuest() {
		const nameEl = this.shadowRoot.querySelector("#profile-name");
		if (nameEl) nameEl.textContent = "Invitado";

		const avatarEl = this.shadowRoot.querySelector("#profile-avatar-text");
		if (avatarEl) avatarEl.textContent = "?";

		const emailEl = this.shadowRoot.querySelector("#profile-email");
		if (emailEl) emailEl.hidden = true;

		const karmaNumEl = this.shadowRoot.querySelector("#profile-karma-num");
		if (karmaNumEl) {
			karmaNumEl.textContent = "—";
			karmaNumEl.classList.add("karma-card__number--empty");
		}

		const levelEl = this.shadowRoot.querySelector("#profile-karma-level-text");
		if (levelEl) levelEl.textContent = "Sin cuenta";

		const fillEl = this.shadowRoot.querySelector("#profile-karma-bar-fill");
		if (fillEl) fillEl.style.width = "0%";

		const nextEl = this.shadowRoot.querySelector("#profile-karma-next");
		if (nextEl) nextEl.textContent = "Inicia sesión para ganar karma";

		// Hide limited warning; blank out the stat tiles for guests
		const limitedEl = this.shadowRoot.querySelector("#profile-limited");
		if (limitedEl) limitedEl.hidden = true;

		this._setStat("#stat-karma", "—");
		this._setStat("#stat-strikes", "—");
		this._setStat("#stat-status", "Invitado");
		this.shadowRoot
			.querySelectorAll(".profile-stat--warn")
			.forEach((el) => el.classList.remove("profile-stat--warn"));

		const statsLink = this.shadowRoot.querySelector('[data-action="go-stats"]');
		if (statsLink) {
			statsLink.style.opacity = "0.5";
			statsLink.style.pointerEvents = "none";
		}

		this._renderThemeToggle();
	}

	_renderThemeToggle() {
		const currentTheme = document.documentElement.dataset.theme || "night";
		const themeIcon = this.shadowRoot.querySelector("#theme-icon");
		const themeHint = this.shadowRoot.querySelector("#theme-hint");

		if (themeIcon) {
			themeIcon.className =
				currentTheme === "night" ? "ph ph-sun" : "ph ph-moon-stars";
			themeIcon.setAttribute("aria-hidden", "true");
		}
		if (themeHint) {
			themeHint.textContent = currentTheme === "night" ? "Noche" : "Día";
		}
	}

	_toggleTheme() {
		const current = document.documentElement.dataset.theme || "night";
		const next = current === "night" ? "day" : "night";

		// Route through the single theme controller: it persists the choice,
		// runs the transition, updates the PWA meta theme-color, and notifies
		// AppShell's onThemeChange listener (which dispatches SET_THEME) — all
		// of which this used to duplicate by hand, minus the transition/meta
		// parts, which it simply skipped.
		setTheme(next);
		this._renderThemeToggle();
	}

	async _handleLogout() {
		try {
			await logout();
		} catch {
			// already cleared
		}

		dispatch("CLEAR_USER");
		this.hide();

		this.dispatchEvent(
			new CustomEvent("profile:logout", { bubbles: true, composed: true }),
		);

		router.navigate("/");
	}

	_getInitials(user) {
		const parts = [user.first_name, user.last_name].filter(Boolean);
		if (parts.length === 0) return "?";
		return parts
			.map((p) => (p[0] || "").toUpperCase())
			.join("")
			.slice(0, 2);
	}

	_calcKarma(karma) {
		const milestones = [10, 50, 100, 250, 500];
		// Level names line up with the milestone thresholds above
		const levels = [
			"Novato",
			"Vecino",
			"Colaborador",
			"Confiable",
			"Guardián",
			"Leyenda",
		];

		const nextMilestone = milestones.find((m) => m > karma) || karma + 10;
		let prevMilestone = 0;
		let levelIndex = 0;

		for (let i = milestones.length - 1; i >= 0; i--) {
			if (milestones[i] <= karma) {
				prevMilestone = milestones[i];
				levelIndex = i + 1;
				break;
			}
		}

		const range = nextMilestone - prevMilestone;
		const progress =
			range > 0 ? Math.min(100, ((karma - prevMilestone) / range) * 100) : 100;

		return { nextMilestone, progress, level: levels[levelIndex] };
	}

	_close() {
		this.hide();
		this.dispatchEvent(
			new CustomEvent("profile:close", { bubbles: true, composed: true }),
		);
	}
}

if (!customElements.get("profile-screen")) {
	customElements.define("profile-screen", ProfileScreen);
}
